import React, { useState } from 'react';

import { post } from 'helpers/api';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';
import { Select } from 'components/ui/select';
import { Button } from 'components/ui/button';
import type { Metrics } from 'components/ai/assistant/types';
import SummaryBody from './summary_body';

export interface ModelOption {
  id: string;
  name: string;
}

interface Props {
  path: string;
  errorMessage: string;
  models: ModelOption[];
}

type Payload = { success: true; summary: string; metrics?: Metrics } | { success: false };

export default function ModelComparison({ path, errorMessage, models }: Props) {
  const [model, setModel] = useState<ModelOption | null>(models[0] ?? null);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  const { subscribe, unsubscribe } = useOneOffSubscription();

  const handleSubmit = () => {
    if (!model) {
      return;
    }

    setLoading(true);
    setError(false);
    setSummary('');
    setMetrics(null);

    subscribe<Payload>({
      async connected(requestId) {
        const response = await post(path, { requestId, model: model.id });
        if (response?.summary) {
          // Cache hit — no worker will broadcast.
          unsubscribe();
          setSummary(response.summary);
          setMetrics(response.metrics ?? null);
          setLoading(false);
        }
      },
      connectedError() {
        setError(true);
        setLoading(false);
      },
      received(payload) {
        if (payload.success) {
          setSummary(payload.summary);
          setMetrics(payload.metrics ?? null);
        } else {
          setError(true);
        }
        setLoading(false);
      },
    });
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="flex-grow-1">
          <Select<ModelOption>
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            isSearchable={false}
            options={models}
            value={model}
            onChange={option => option && setModel(option)}
          />
        </div>
        <Button disabled={loading || !model} label="Submit" onClick={handleSubmit} />
      </div>
      <SummaryBody
        emptyMessage="Select a model and submit to compare."
        error={error}
        errorMessage={errorMessage}
        loading={loading}
        metrics={metrics}
        summary={summary}
      />
    </>
  );
}
